import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";

import { callLlm } from "../api/llm/LlmService";
import { getToken } from "../components/commonmethod/CommonMethod";

const BASE_URL = import.meta.env.VITE_BASE_URL;
const DEFAULT_ERROR_MESSAGE = "요약 생성 중 오류가 발생했습니다.";

const useLlmData = ({ keys, enabled, search }) => {
    const esRef = useRef(null);

    const [data, setData] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [statusText, setStatusText] = useState("");

    const uri = useMemo(() => (Array.isArray(keys) ? keys[0] : keys), [keys]);

    const closeEventSource = useCallback(() => {
        if (esRef.current) {
            try {
                esRef.current.close();
            } catch (_) {
                // no-op
            }
            esRef.current = null;
        }
    }, []);

    const fetchData = useCallback(async () => {
        if (!enabled || !uri) return;

        setData("");
        setError(false);
        setLoading(true);
        setStatusText("요약 생성 중..");
        closeEventSource();

        const token = getToken();
        const options = {
            headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            withCredentials: true,
            heartbeatTimeout: 60000,
        };

        const es = new EventSourcePolyfill(`${BASE_URL}/sse/connect`, options);
        esRef.current = es;

        es.addEventListener("status", (event) => {
            setStatusText(event.data || "");
        });

        es.addEventListener("delta", (event) => {
            setData((prev) => `${prev || ""}${event.data || ""}`);
            setLoading(true);
        });

        es.addEventListener("final", (event) => {
            setData(event.data || "");
            setLoading(false);
            setStatusText("완료");
            closeEventSource();
        });

        es.onerror = (event) => {
            const message =
                event?.data ||
                event?.error?.message ||
                (event?.status
                    ? `SSE 오류 (${event.status}${event.statusText ? `: ${event.statusText}` : ""})`
                    : DEFAULT_ERROR_MESSAGE);

            setError(true);
            setLoading(false);
            setStatusText(message);
            closeEventSource();
        };

        try {
            await callLlm(uri, search);
        } catch (e) {
            setError(true);
            setLoading(false);
            console.log(e);
            setStatusText(DEFAULT_ERROR_MESSAGE);
            closeEventSource();
            return;
        }
    }, [closeEventSource, enabled, search, uri]);

    useEffect(() => {
        if (!enabled) return;
        fetchData();
    }, [enabled, fetchData]);

    useEffect(() => {
        return () => {
            closeEventSource();
        };
    }, [closeEventSource]);

    return { data, loading, error, statusText, fetchData };
};

export default useLlmData;
